import React from 'react';
import { Card, Dropdown, Tag, Button, Modal } from 'antd';
import type { MenuProps } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { User } from '../services/adminService';

const roleColor = (role: string): string => {
  switch (role) {
    case 'super_admin':
      return 'red';
    case 'reseller_admin':
      return 'orange';
    case 'reseller_support':
      return 'gold';
    case 'customer_admin':
      return 'blue';
    case 'asset_manager':
      return 'green';
    case 'department_head':
      return 'cyan';
    case 'crew':
      return 'default';
    default:
      return 'default';
  }
};

export type UserListCardHandlers = {
  onEdit: (user: User) => void;
  onDelete: (id: number) => void;
};

type UserListCardProps = {
  user: User;
  handlers: UserListCardHandlers;
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0, 0, 0, 0.55)', minWidth: 90, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

const UserListCard: React.FC<UserListCardProps> = ({ user, handlers }) => {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();

  const menuItems: MenuProps['items'] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: () => handlers.onEdit(user),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () =>
        Modal.confirm({
          title: 'Deactivate user',
          icon: <ExclamationCircleOutlined />,
          content: 'Are you sure you want to deactivate this user?',
          okText: 'Yes, deactivate',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: () => handlers.onDelete(user.id),
        }),
    },
  ];

  return (
    <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { padding: 12 } }}>
      {/* Top row: email (primary identifier, clickable) + kebab */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 500, wordBreak: 'break-all' }}>
          <Link to={`/admin/users/${user.id}`}>{user.email}</Link>
        </div>
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
            aria-label={`Actions for ${user.email}`}
            style={{ flexShrink: 0 }}
          />
        </Dropdown>
      </div>

      {/* Body: label/value rows */}
      {name && <FieldRow label="Name">{name}</FieldRow>}
      <FieldRow label="Role">
        <Tag color={roleColor(user.role)} style={{ margin: 0 }}>
          {user.role}
        </Tag>
      </FieldRow>
      <FieldRow label="Organization">{user.org_name || '\u2014'}</FieldRow>
      <FieldRow label="Status">
        <Tag color={user.is_active ? 'green' : 'red'} style={{ margin: 0 }}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Tag>
      </FieldRow>
    </Card>
  );
};

export default UserListCard;
